import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Plus, PlusCircle, ChevronRight, FolderOpen, Pencil, Settings2, X, Check, Archive,
    Coins, TrendingDown, FolderX, Calculator, AlertTriangle,
} from 'lucide-react';
import RecipeKatIcon from './RecipeKatIcon';
import RecipeValidaceBadge, { RecipeValidaceCounts } from './RecipeValidaceBadge';
import { ListSkeleton, HlavickaUzka, RozbalitVse, pasmoMarze, stavReceptury, stariRevize } from './RecipeListPanel';
import { StavBunka } from './PrehledBunky';
import { PRAH_MARZE, MARZE_BARVY, stavMarze } from './prehledGrid';
import { AlergenyBunka } from '../spizirna/MaterialPrehledBunky';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry, pocetAktivnichFiltru } from '../layout/ListFiltr';
import { aggregateValidace, fmtCislo, fmtDate, SECTION_COLOR } from './receptUtils';
import { useVyberPolozek, nastavNahledTazeni, pocetPolozek } from './useVyberPolozek';
import {
    GRID_ING, priplatekRozpeti, priplatekBezDph, marzeIngredience, stavAlergenuIngredience,
    useViditelneSloupceIngredienci,
} from './ingredienceGrid';

const BEZ_SKUPINY = '__bez__';
const INDENT = 15; // px odsazení položek pod hlavičkou skupiny
const VYBER_COLOR = '#3B82F6';

// Hodnoty skupin filtru: všechno volby (NEBO uvnitř skupiny).
const PRAZDNE_FILTRY = { skupina: [], marze: [], stav: [], revize: [], alergeny: [], alergen: [] };

const fmtKc = (v) => `${fmtCislo(v).replace(/,00$/, '')} Kč`;
const fmtRozpeti = (r) => (!r ? null : r.min === r.max ? fmtKc(r.min) : `${fmtCislo(r.min).replace(/,00$/, '')}–${fmtKc(r.max)}`);
const sklonujIngredience = (n) => `${n} ${n === 1 ? 'ingredience' : (n >= 2 && n <= 4 ? 'ingredience' : 'ingrediencí')}`;

// ── Buňky mřížky ────────────────────────────────────────────────────────────

/**
 * Náklad → příplatek s DPH. Proužek pod čísly porovnává náklad s cenou BEZ DPH
 * (to, co firmě zůstane) — s cenou s DPH by marže vypadala o daň lepší.
 */
function PriplatekBunka({ naklad, rozpeti, bezDph, jednotka, souhrn = false, mutedColor, isDark }) {
    const maCenu = !!rozpeti;
    const podil = maCenu && bezDph > 0 && naklad > 0 ? Math.max(0, Math.min(1, naklad / bezDph)) : null;
    const prekroceno = podil != null && naklad > bezDph;

    return (
        <div style={{ width: GRID_ING.priplatek, flexShrink: 0, textAlign: 'right' }}
            title={maCenu ? 'Náklad (bez DPH) → příplatek, který platí host (s DPH)' : 'Příplatek nemá platnou cenu'}>
            <div className="text-[12px] tabular-nums" style={{ fontFamily: 'ui-monospace, monospace' }}>
                {!souhrn && (
                    <>
                        <span style={{ color: mutedColor }}>{naklad != null ? fmtCislo(naklad) : '—'}</span>
                        <span style={{ color: mutedColor }}> → </span>
                    </>
                )}
                {maCenu
                    ? <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{fmtRozpeti(rozpeti)}</span>
                    : souhrn
                        ? <span style={{ color: mutedColor }}>—</span>
                        : <span className="font-semibold" style={{ color: MARZE_BARVY.kriticka }}>bez ceny</span>}
                {!souhrn && maCenu && jednotka && <span className="text-[10px]" style={{ color: mutedColor }}>/{jednotka}</span>}
            </div>
            {podil != null && !souhrn && (
                <div className="flex" style={{ height: 3, marginTop: 4, gap: 2 }}>
                    <div style={{ width: `${podil * 100}%`, borderRadius: 2, background: prekroceno ? MARZE_BARVY.kriticka : (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.22)') }} />
                    <div style={{ flex: 1, borderRadius: 2, background: prekroceno ? 'transparent' : MARZE_BARVY.dobra }} />
                </div>
            )}
        </div>
    );
}

/** Marže v % — barvu nese jen dobrá a podprahová, jako u produktů (PRAH_MARZE). */
function MarzeIngBunka({ pct, souhrn = false, mutedColor, isDark }) {
    const stav = stavMarze(pct);
    const barva = MARZE_BARVY[stav];
    const popis = pct == null ? 'Bez ceny — marže se nepočítá'
        : `${souhrn ? 'Nejhorší marže ve skupině' : 'Marže (nejhorší kanál)'}: ${Math.round(pct)} %`;
    return (
        <div className="flex items-center justify-end" style={{ width: GRID_ING.marze, flexShrink: 0 }} title={popis}>
            {souhrn ? (
                <span className="text-[11px] tabular-nums" style={{ color: barva || mutedColor, fontWeight: barva ? 600 : 400 }}>
                    {pct == null ? '—' : `${Math.round(pct)} %`}
                </span>
            ) : (
                <span className="text-[11px] font-semibold tabular-nums inline-flex items-center justify-center"
                    style={{
                        minWidth: 52, height: 20, borderRadius: 5, gap: 2, padding: '0 6px',
                        color: barva || 'var(--text-primary)',
                        background: barva ? `${barva}${isDark ? '2e' : '1f'}` : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                    }}>
                    {stav === 'kriticka' && <AlertTriangle size={9} />}
                    {pct == null ? '—' : `${Math.round(pct)} %`}
                </span>
            )}
        </div>
    );
}

/** Hlavička mřížky — popisky jednou nad seznamem, šířky z GRID_ING. */
function HlavickaMrizkyIngredienci({ isDark, sloupce, rozbaleni }) {
    const l = 'text-[10px] font-semibold uppercase tracking-wider truncate';
    const barva = isDark ? 'rgba(255,255,255,0.62)' : 'var(--text-secondary)';
    const bunka = (label, sirka, zarovnani = 'left') => (
        <div className={l} style={{ width: sirka, flexShrink: 0, textAlign: zarovnani }}>{label}</div>
    );
    return (
        <div className="flex items-end"
            style={{
                gap: GRID_ING.mezera, padding: '7px 10px 6px 13px', color: barva,
                background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
            }}>
            <div className="flex items-center justify-center" style={{ width: GRID_ING.uchyt, flexShrink: 0, alignSelf: 'center' }}>
                <RozbalitVse rozbaleni={rozbaleni} mutedColor={barva} />
            </div>
            {sloupce?.nahled !== false && <div style={{ width: GRID_ING.nahled, flexShrink: 0 }} />}
            <div className={l} style={{ flex: `${GRID_ING.polozkaRust} 1 0`, minWidth: GRID_ING.polozkaMin }}>ingredience</div>
            {sloupce?.skupiny !== false && bunka('skupiny', GRID_ING.skupiny)}
            {bunka('náklad → příplatek s DPH', GRID_ING.priplatek, 'right')}
            {bunka('marže', GRID_ING.marze, 'right')}
            <div className={l} style={{ flex: '1 1 0', minWidth: GRID_ING.alergeny }}>alergeny</div>
            {sloupce?.revize !== false && bunka('revize', GRID_ING.revize, 'right')}
            {bunka('stav', GRID_ING.stav, 'right')}
        </div>
    );
}

// ── Řádek ingredience ───────────────────────────────────────────────────────
// `siroky` = řádek mřížky přehledu, jinak dvouřádková karta (úzký sloupec, mobil).
function IngredienceRadek({
    rec, active, selected, depthPad = 0, onClick, onDragStart, siroky, kompaktni, sloupce,
    isDark, mutedColor,
}) {
    const jed = rec.jednotka?.zkratka || rec.jednotka_zkratka;
    const rozpeti = priplatekRozpeti(rec);
    const marze = marzeIngredience(rec);
    const skupinyNazvy = (rec.pridavky_skupiny || []).map(s => s.nazev);
    const ingr = rec.kusovnik_count;
    // Problém = náklad přerostl příplatek bez DPH. Nic měkčího se červeně nemaluje.
    const bezDph = priplatekBezDph(rec);
    const problem = siroky && bezDph > 0 && rec.total_cost > bezDph;

    const podklad = selected
        ? (isDark ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.10)')
        : active ? (isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.06)')
        : problem ? (isDark ? 'rgba(239,68,68,0.09)' : 'rgba(239,68,68,0.05)')
        : 'transparent';

    const spolecne = {
        'data-list-id': rec.id,
        onClick,
        // HTML5 drag & drop na dotyku nefunguje — na mobilu se řádek netahá vůbec.
        draggable: !!onDragStart,
        onDragStart: onDragStart ? (e => { onDragStart(e, rec); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(rec.id)); } catch { /* noop */ } }) : undefined,
        className: `flex items-center cursor-pointer transition-colors ${siroky ? '' : 'gap-2.5'}`,
        style: siroky ? {
            gap: GRID_ING.mezera, padding: '7px 10px', paddingLeft: 10 + depthPad,
            borderLeft: `3px solid ${selected ? VYBER_COLOR : (active ? SECTION_COLOR : (problem ? MARZE_BARVY.kriticka : 'transparent'))}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            background: podklad, opacity: rec.aktivni === false ? 0.6 : 1, userSelect: 'none',
        } : {
            padding: '8px 10px', marginLeft: depthPad,
            minHeight: kompaktni ? 56 : undefined,
            borderLeft: `3px solid ${selected ? VYBER_COLOR : (active ? SECTION_COLOR : 'transparent')}`,
            background: podklad, opacity: rec.aktivni === false ? 0.6 : 1, userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
        },
        // Hover jen pro myš — na dotyku by zůstal viset na posledním klepnutém řádku.
        onMouseEnter: kompaktni ? undefined : (e => { if (!active && !selected) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)'; }),
        onMouseLeave: kompaktni ? undefined : (e => { if (!active && !selected) e.currentTarget.style.background = podklad; }),
    };

    const ikona = (
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
            <RecipeKatIcon foto={rec.foto_url} ikona={rec.ikona} kategorieNazev={skupinyNazvy[0] || rec.nazev}
                size={siroky ? 40 : 36} radius={siroky ? 10 : undefined} />
            {selected && (
                <span style={{
                    position: 'absolute', top: -4, left: -4, width: 16, height: 16, borderRadius: '50%',
                    background: VYBER_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 0 2px ${isDark ? '#1a1a26' : '#fff'}`,
                }}>
                    <Check size={10} strokeWidth={3} style={{ color: '#fff' }} />
                </span>
            )}
        </span>
    );

    const archivOdznak = rec.aktivni === false && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ background: 'rgba(148,163,184,0.16)', color: '#94A3B8' }}>Archiv</span>
    );

    if (siroky) {
        const meta = [ingr != null ? `${ingr} ingr.` : null, jed ? `za ${jed}` : null].filter(Boolean);
        return (
            <div {...spolecne}>
                <div style={{ width: GRID_ING.uchyt, flexShrink: 0 }} />
                {sloupce?.nahled !== false && <div style={{ width: GRID_ING.nahled, flexShrink: 0, display: 'flex' }}>{ikona}</div>}
                <div className="min-w-0" style={{ flex: `${GRID_ING.polozkaRust} 1 0`, minWidth: GRID_ING.polozkaMin }}>
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{rec.nazev}</span>
                        <span className="text-[10px] font-mono" style={{ color: mutedColor, flexShrink: 0 }}>{rec.kod}</span>
                        {archivOdznak}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: mutedColor, marginTop: 1 }}>{meta.join(' · ') || ' '}</div>
                </div>
                {sloupce?.skupiny !== false && (
                    <div className="text-[11px] truncate" style={{ width: GRID_ING.skupiny, flexShrink: 0, color: skupinyNazvy.length ? 'var(--text-primary)' : mutedColor }}
                        title={skupinyNazvy.join(', ') || 'Není v žádné skupině — nikde se nenabízí'}>
                        {skupinyNazvy.join(', ') || 'bez skupiny'}
                    </div>
                )}
                <PriplatekBunka naklad={rec.total_cost} rozpeti={rozpeti} bezDph={bezDph} jednotka={jed} mutedColor={mutedColor} isDark={isDark} />
                <MarzeIngBunka pct={marze} mutedColor={mutedColor} isDark={isDark} />
                <AlergenyBunka alergeny={rec.alergeny || []} bezAlergenu={stavAlergenuIngredience(rec) === 'bez'} mutedColor={mutedColor} isDark={isDark} />
                {sloupce?.revize !== false && (
                    <div className="text-[11px] tabular-nums text-right" style={{ width: GRID_ING.revize, flexShrink: 0, color: mutedColor, fontFamily: 'ui-monospace, monospace' }}>
                        {rec.posledni_revize_at ? fmtDate(rec.posledni_revize_at) : '—'}
                    </div>
                )}
                <StavBunka validace={rec.validace} mutedColor={mutedColor} />
            </div>
        );
    }

    // Úzký sloupec / mobil: dvouřádková karta — podle čeho se vybírá.
    const metaBits = [skupinyNazvy.join(', ') || 'bez skupiny', ingr != null ? `${ingr} ingr.` : null].filter(Boolean);
    const marzeBarva = MARZE_BARVY[stavMarze(marze)];
    return (
        <div {...spolecne}>
            {ikona}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <RecipeValidaceBadge validace={rec.validace} size={15} />
                    <span className="text-[13px] font-semibold flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{rec.nazev}</span>
                    <span className="text-[10px] font-mono" style={{ color: mutedColor, flexShrink: 0 }}>{rec.kod}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] flex-1 min-w-0 truncate" style={{ color: mutedColor }}>{metaBits.join(' · ')}</span>
                    <div className="flex items-center gap-1.5" style={{ flexShrink: 0 }}>
                        {archivOdznak}
                        {rozpeti ? (
                            <>
                                <span className="text-[12px] font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>+{fmtRozpeti(rozpeti)}</span>
                                {marze != null && (
                                    <span className="text-[11px] tabular-nums" style={{ color: marzeBarva || mutedColor, fontWeight: marzeBarva ? 600 : 400 }}>{Math.round(marze)} %</span>
                                )}
                            </>
                        ) : (
                            <span className="text-[11px]" style={{ color: MARZE_BARVY.kriticka }} title="Příplatek nemá platnou cenu">bez ceny</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Seznam ingrediencí (příplatků, ADD) — první sloupec standardního rozvržení
 * modulu (norma UI, standard-rozvrzeni.md; vzor SurovinyListPanel).
 *
 * Ingredience nemají kategorie, ale **skupiny** (plochý číselník) — a jedna
 * ingredience může být ve víc skupinách naráz, takže se stejný řádek objeví
 * ve víc sekcích. Přetažení proto neznamená „nastav jedinou skupinu", ale
 * „ber ze zdrojové, přidej do cílové" — ostatní zařazení zůstanou.
 *
 * `siroky` = přehled přes celou šířku (nic není vybráno): řádky se rozloží do
 * sloupcové mřížky a hlavička skupiny nese souhrn ve stejných sloupcích.
 * `kompaktni` = mobil: bez přetahování, bez úpravy skupin, řádky 56 px.
 *
 * `onPoradi` dostane id v pořadí, v jakém je člověk vidí — podle něj jezdí
 * šipky ↑↓ v detailu (pohled, filtr a sbalené skupiny se tak respektují).
 */
export default function IngredienceListPanel({
    isDark, mutedColor, secondaryColor, cardStyle, inputStyle, siroky = false, kompaktni = false,
    search, setSearch, skupiny, items, loading,
    activeId, onSelect, onNew, onEditSkupina, onManageSkupiny, onMoveToSkupina, onSkupinaKlik, onPoradi,
    rezervaDole = null,
}) {
    const searchActive = !!search.trim();
    const sloupce = useViditelneSloupceIngredienci(siroky);
    const vyber = useVyberPolozek('pridavky');
    const lzeTahat = !!onMoveToSkupina && !kompaktni;

    const [expanded, setExpanded] = useState(() => new Set());
    const [pohled, setPohled] = useState('aktivni');
    const [filtry, setFiltry] = useState(PRAZDNE_FILTRY);
    const [drag, setDrag] = useState(null);            // { ids, zdrojId } — co se táhne
    const [dragOverSkupina, setDragOverSkupina] = useState(null);

    const skupinySorted = useMemo(() => [...(skupiny || [])]
        .sort((a, b) => (a.poradi ?? 0) - (b.poradi ?? 0) || (a.nazev || '').localeCompare(b.nazev || '', 'cs')),
    [skupiny]);
    const znameSkupiny = useMemo(() => new Set(skupinySorted.map(s => s.id)), [skupinySorted]);
    const skupinyPolozky = useCallback((it) => (it.pridavky_skupiny || []).map(s => s.id).filter(id => znameSkupiny.has(id)), [znameSkupiny]);

    const hledane = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (items || [])
            .filter(it => !q || it.nazev?.toLowerCase().includes(q) || it.kod?.toLowerCase().includes(q))
            .sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
    }, [items, search]);

    const { aktivni, archiv } = useMemo(() => {
        const akt = [], arch = [];
        for (const it of hledane) (it.aktivni === false ? arch : akt).push(it);
        return { aktivni: akt, archiv: arch };
    }, [hledane]);

    const odpovidaVolbe = useCallback((skupina, it, volba) => {
        switch (skupina) {
            case 'skupina': {
                const ids = skupinyPolozky(it);
                return volba === BEZ_SKUPINY ? ids.length === 0 : ids.includes(volba);
            }
            case 'marze': return pasmoMarze(it) === volba;
            case 'stav': return stavReceptury(it) === volba;
            case 'revize': return stariRevize(it) === volba;
            case 'alergeny': return stavAlergenuIngredience(it) === volba;
            case 'alergen': return (it.alergeny || []).some(a => a.id === volba);
            default: return true;
        }
    }, [skupinyPolozky]);

    // `vynech` = skupina, kterou nepočítat — pro počty u voleb té skupiny.
    const odpovidaFiltrum = useCallback((it, f, vynech = null) => {
        for (const skupina of Object.keys(PRAZDNE_FILTRY)) {
            if (skupina === vynech || !f[skupina].length) continue;
            if (!f[skupina].some(v => odpovidaVolbe(skupina, it, v))) return false;
        }
        return true;
    }, [odpovidaVolbe]);

    // Předdefinované pohledy — co se u příplatků řeší nejčastěji.
    const pohledy = useMemo(() => ({
        aktivni,
        bezPriplatku: aktivni.filter(it => !priplatekRozpeti(it)),
        marze: aktivni.filter(it => pasmoMarze(it) === 'pod'),
        bezSkupiny: aktivni.filter(it => skupinyPolozky(it).length === 0),
        bezKalkulace: aktivni.filter(it => it.total_cost == null),
        archiv,
    }), [aktivni, archiv, skupinyPolozky]);

    const vRezimuArchiv = pohled === 'archiv';
    // Bez skupiny i archiv se kreslí naplocho — skupina u nich nic neříká.
    const naplocho = vRezimuArchiv || pohled === 'bezSkupiny';
    const pohledSada = pohledy[pohled] || aktivni;
    const zobrazene = useMemo(() => pohledSada.filter(it => odpovidaFiltrum(it, filtry)), [pohledSada, filtry, odpovidaFiltrum]);

    const skupinyFiltru = useMemo(() => {
        const sPocty = (id, volby) => volby.map(v => ({
            ...v,
            pocet: pohledSada.filter(it => odpovidaFiltrum(it, filtry, id) && odpovidaVolbe(id, it, v.value)).length,
        }));
        // Konkrétní alergeny jen ty, které se v ingrediencích opravdu vyskytují.
        const alergeny = new Map();
        for (const it of items || []) for (const a of it.alergeny || []) alergeny.set(a.id, a);
        const alergenVolby = [...alergeny.values()]
            .sort((a, b) => (a.cislo_eu ?? 99) - (b.cislo_eu ?? 99))
            .map(a => ({ value: a.id, label: `${a.cislo_eu} ${a.nazev}` }));

        return [
            { id: 'skupina', label: 'Skupina', typ: 'volby', volby: sPocty('skupina', [
                ...skupinySorted.map(s => ({ value: s.id, label: s.nazev })),
                { value: BEZ_SKUPINY, label: 'Bez skupiny' },
            ]) },
            { id: 'marze', label: 'Marže (nejhorší kanál)', labelKratky: 'Marže', typ: 'volby', volby: sPocty('marze', [
                { value: 'pod', label: `Pod ${PRAH_MARZE.kriticka} %` },
                { value: 'stredni', label: `${PRAH_MARZE.kriticka}–${PRAH_MARZE.dobra} %` },
                { value: 'nad', label: `Nad ${PRAH_MARZE.dobra} %` },
                { value: 'bez', label: 'Bez příplatku' },
            ]) },
            { id: 'stav', label: 'Stav receptury', labelKratky: 'Stav', typ: 'volby', volby: sPocty('stav', [
                { value: 'ok', label: 'V pořádku' }, { value: 'varovani', label: 'Varování' }, { value: 'chyba', label: 'Chyba' },
            ]) },
            { id: 'revize', label: 'Revize', typ: 'volby', volby: sPocty('revize', [
                { value: 'aktualni', label: 'Do 6 měsíců' }, { value: 'stara', label: 'Starší než 6 měsíců' }, { value: 'nikdy', label: 'Nikdy' },
            ]) },
            { id: 'alergeny', label: 'Alergeny', typ: 'volby', volby: sPocty('alergeny', [
                { value: 'obsahuje', label: 'Obsahuje alergeny' }, { value: 'bez', label: 'Bez alergenů' }, { value: 'nevyplneno', label: 'Bez receptury' },
            ]) },
            ...(alergenVolby.length ? [{ id: 'alergen', label: 'Obsahuje alergen', labelKratky: 'Alergen', typ: 'volby', volby: sPocty('alergen', alergenVolby) }] : []),
        ];
    }, [items, skupinySorted, pohledSada, filtry, odpovidaFiltrum, odpovidaVolbe]);

    const pocetFiltru = pocetAktivnichFiltru(skupinyFiltru, filtry);
    const zmenFiltr = (id, hodnota) => setFiltry(f => ({ ...f, [id]: hodnota }));
    const zrusFiltry = () => setFiltry(PRAZDNE_FILTRY);

    // Skupiny se zúženou sadou schovají prázdné a rozbalí se samy.
    const filtrAktivni = searchActive || pohled !== 'aktivni' || pocetFiltru > 0;

    // Rozřazení do skupin. Jedna ingredience může být ve víc skupinách.
    const { bySkupina, bez } = useMemo(() => {
        const map = new Map();
        const bezSkupiny = [];
        for (const it of zobrazene) {
            const ids = skupinyPolozky(it);
            if (ids.length === 0) { bezSkupiny.push(it); continue; }
            for (const id of ids) {
                if (!map.has(id)) map.set(id, []);
                map.get(id).push(it);
            }
        }
        return { bySkupina: map, bez: bezSkupiny };
    }, [zobrazene, skupinyPolozky]);

    const allKeys = useMemo(() => [...skupinySorted.map(s => s.id), BEZ_SKUPINY], [skupinySorted]);
    const allExpanded = allKeys.length > 0 && allKeys.every(k => expanded.has(k));
    const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(allKeys));
    const toggle = (key) => setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const rozbaleni = skupinySorted.length > 0 && !filtrAktivni && !naplocho ? { vse: allExpanded, prepni: toggleAll } : null;
    const jeOtevrena = (key) => filtrAktivni || expanded.has(key);

    // Klik na název skupiny: z detailu vede zpátky na přehled (norma §1), jinak
    // obyčejné rozbalení. Neuložené změny drží detail otevřený.
    const klikNaSkupinu = async (key) => {
        const vysledek = onSkupinaKlik ? await onSkupinaKlik() : 'nic';
        if (vysledek === 'blokovano') return;
        if (vysledek === 'prehled') { setExpanded(prev => new Set(prev).add(key)); return; }
        toggle(key);
    };

    // Otevři skupinu, ve které leží vybraná položka — jinak by po výběru
    // z hledání „zmizela" do zabalené sekce.
    useEffect(() => {
        if (!activeId || activeId === 'new') return;
        const it = (items || []).find(x => x.id === activeId);
        if (!it) return;
        const ids = skupinyPolozky(it);
        setExpanded(prev => {
            const n = new Set(prev);
            if (ids.length === 0) n.add(BEZ_SKUPINY); else ids.forEach(id => n.add(id));
            return n.size === prev.size ? prev : n;
        });
    }, [activeId, items, skupinyPolozky]);

    // Pořadí, v jakém řádky vidí člověk (bez duplicit) — pro šipky ↑↓.
    const poradi = useMemo(() => {
        if (naplocho) return zobrazene.map(it => it.id);
        const ids = [];
        const videno = new Set();
        const pridej = (list) => { for (const it of list) if (!videno.has(it.id)) { videno.add(it.id); ids.push(it.id); } };
        for (const s of skupinySorted) if (jeOtevrena(s.id)) pridej(bySkupina.get(s.id) || []);
        if (jeOtevrena(BEZ_SKUPINY)) pridej(bez);
        return ids;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [naplocho, zobrazene, skupinySorted, bySkupina, bez, expanded, filtrAktivni]);
    useEffect(() => { onPoradi?.(poradi); }, [poradi, onPoradi]);

    // ── Přetahování mezi skupinami (jen desktop) ──
    const zacniTazeni = (e, rec, zdrojId) => {
        const ids = vyber.vybrane.has(rec.id) ? [...vyber.vybrane] : [rec.id];
        setDrag({ ids, zdrojId });
        nastavNahledTazeni(e, ids.length);
    };
    const dropProps = (cilId) => (lzeTahat ? {
        onDragOver: (e) => { if (drag) { e.preventDefault(); e.stopPropagation(); setDragOverSkupina(cilId); } },
        onDragLeave: () => setDragOverSkupina(c => (c === cilId ? null : c)),
        onDrop: (e) => {
            e.preventDefault(); e.stopPropagation();
            setDragOverSkupina(null);
            const d = drag;
            setDrag(null);
            if (!d?.ids?.length || d.zdrojId === cilId) return;
            onMoveToSkupina(d.ids, cilId, d.zdrojId);
            vyber.zrus();
        },
    } : {});
    const dropStyl = (cilId) => (dragOverSkupina === cilId ? {
        background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.10)',
        outline: `1px dashed ${SECTION_COLOR}`,
    } : {});

    const radek = (it, zdrojId = null, depthPad = INDENT - 2) => (
        <IngredienceRadek key={`${zdrojId ?? 'x'}-${it.id}`} rec={it}
            active={activeId === it.id} selected={vyber.vybrane.has(it.id)}
            depthPad={siroky ? 0 : depthPad} siroky={siroky} kompaktni={kompaktni} sloupce={sloupce}
            onClick={(e) => vyber.klikNaRadek(e, it.id, () => onSelect(it.id))}
            onDragStart={lzeTahat ? ((e, rec) => zacniTazeni(e, rec, zdrojId)) : undefined}
            isDark={isDark} mutedColor={mutedColor} />
    );

    const VodiciLinka = () => (siroky ? null : (
        <span aria-hidden style={{
            position: 'absolute', left: 14, top: 0, bottom: 0, width: 1,
            background: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)', pointerEvents: 'none',
        }} />
    ));

    // Souhrn skupiny v přehledu — ve stejných sloupcích jako položky.
    const souhrnBunky = (list) => {
        const alergeny = new Map();
        let nevyplneno = 0, problemu = 0, min = null, max = null, nejhorsi = null;
        for (const it of list) {
            for (const a of it.alergeny || []) alergeny.set(a.id, a);
            if (stavAlergenuIngredience(it) === 'nevyplneno') nevyplneno++;
            if (it.validace?.stav === 'red') problemu++;
            const r = priplatekRozpeti(it);
            if (r) { min = min == null ? r.min : Math.min(min, r.min); max = max == null ? r.max : Math.max(max, r.max); }
            const m = marzeIngredience(it);
            if (m != null) nejhorsi = nejhorsi == null ? m : Math.min(nejhorsi, m);
        }
        return (
            <>
                {sloupce?.skupiny !== false && <div style={{ width: GRID_ING.skupiny, flexShrink: 0 }} />}
                <PriplatekBunka souhrn rozpeti={min != null ? { min, max } : null} mutedColor={mutedColor} isDark={isDark} />
                <MarzeIngBunka souhrn pct={nejhorsi} mutedColor={mutedColor} isDark={isDark} />
                <AlergenyBunka souhrn alergeny={[...alergeny.values()]} nevyplneno={nevyplneno} mutedColor={mutedColor} isDark={isDark} />
                {sloupce?.revize !== false && <div style={{ width: GRID_ING.revize, flexShrink: 0 }} />}
                <StavBunka problemu={problemu} mutedColor={mutedColor} />
            </>
        );
    };

    const sipka = (key, isOpen) => (
        <button onClick={() => toggle(key)} aria-label={isOpen ? 'Sbalit' : 'Rozbalit'}
            style={{
                background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, padding: siroky ? 4 : 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: kompaktni ? 40 : undefined, height: kompaktni ? 44 : undefined, marginRight: kompaktni ? -8 : 0,
            }}>
            <ChevronRight size={kompaktni ? 18 : 16} style={{ color: mutedColor, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
    );

    const pocetPill = (n) => (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: mutedColor, flexShrink: 0, minWidth: 26, textAlign: 'center' }}>{n}</span>
    );

    // Hlavička skupiny — v přehledu řádek mřížky se souhrnem, jinak karta jako
    // kořenová kategorie ve stromu receptů. `skupina = null` → „Bez skupiny".
    const sekce = (skupina, list) => {
        const key = skupina ? skupina.id : BEZ_SKUPINY;
        const isOpen = jeOtevrena(key);
        if (filtrAktivni && list.length === 0) return null;
        if (!skupina && list.length === 0) return null;
        const nazev = skupina ? skupina.nazev : 'Bez skupiny';
        const popis = skupina
            ? [skupina.typ_vyberu === 'single' ? 'jedna volba' : 'více voleb', skupina.povinny ? 'povinná' : 'volitelná', skupina.aktivni === false ? 'vypnutá' : null].filter(Boolean).join(' · ')
            : 'nikde se nenabízí';
        const tuzka = skupina && onEditSkupina && !kompaktni && (
            <button onClick={(e) => { e.stopPropagation(); onEditSkupina(skupina); }} title="Upravit skupinu"
                className="flex items-center justify-center rounded-md"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: siroky ? 2 : 4, flexShrink: 0, display: 'flex' }}
                onMouseEnter={e => e.currentTarget.style.color = SECTION_COLOR}
                onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                <Pencil size={14} />
            </button>
        );
        const ikonaSkupiny = skupina
            ? <RecipeKatIcon slozka kategorieNazev={skupina.nazev} size={34} radius={7} />
            : <span className="flex items-center justify-center" style={{ width: 34, height: 34 }}><FolderOpen size={16} style={{ color: mutedColor }} /></span>;

        return (
            <div key={key} style={siroky ? { marginTop: 12 } : undefined}>
                <div className="group flex items-center transition-colors" {...dropProps(skupina ? skupina.id : null)}
                    style={{
                        gap: siroky ? GRID_ING.mezera : 10,
                        padding: siroky ? '8px 10px' : '7px 10px', paddingLeft: siroky ? 10 : (kompaktni ? 10 : 8),
                        borderLeft: siroky ? '3px solid transparent' : undefined,
                        minHeight: kompaktni ? 56 : undefined,
                        opacity: skupina?.aktivni === false ? 0.6 : 1,
                        background: !kompaktni ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent',
                        ...dropStyl(skupina ? skupina.id : null),
                    }}>
                    {siroky ? (
                        <>
                            <div className="flex items-center justify-center" style={{ width: GRID_ING.uchyt, flexShrink: 0 }}>{sipka(key, isOpen)}</div>
                            {sloupce?.nahled !== false && <div style={{ width: GRID_ING.nahled, flexShrink: 0, display: 'flex' }}>{ikonaSkupiny}</div>}
                            <div className="min-w-0 cursor-pointer" style={{ flex: `${GRID_ING.polozkaRust} 1 0`, minWidth: GRID_ING.polozkaMin }}
                                onClick={() => klikNaSkupinu(key)}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[14px] font-semibold truncate" style={{ color: skupina ? 'var(--text-primary)' : secondaryColor }}>{nazev}</span>
                                    {pocetPill(list.length)}
                                    <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">{tuzka}</span>
                                </div>
                                <div className="text-[11px] truncate" style={{ color: mutedColor, marginTop: 1 }}>{sklonujIngredience(list.length)} · {popis}</div>
                            </div>
                            {souhrnBunky(list)}
                        </>
                    ) : (
                        <>
                            <div onClick={() => klikNaSkupinu(key)} className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                                {ikonaSkupiny}
                                <div className="min-w-0">
                                    <span className="text-sm font-semibold truncate block" style={{ color: skupina ? 'var(--text-primary)' : secondaryColor }}>{nazev}</span>
                                    <span className="text-[10px] truncate block" style={{ color: mutedColor }}>{popis}</span>
                                </div>
                            </div>
                            {tuzka}
                            <RecipeValidaceCounts counts={aggregateValidace(list)} />
                            {pocetPill(list.length)}
                            {sipka(key, isOpen)}
                        </>
                    )}
                </div>
                {isOpen && list.length > 0 && (
                    <div style={{ position: 'relative' }}><VodiciLinka />{list.map(it => radek(it, skupina ? skupina.id : null))}</div>
                )}
            </div>
        );
    };

    // Na mobilu jen „Nová ingredience". Správa skupin je stavba struktury —
    // patří na desktop s plnou editací (norma §1b).
    const akce = [
        { id: 'novy', label: 'Nová ingredience', Icon: Plus, onClick: onNew, primarni: true },
        ...(kompaktni || !onManageSkupiny ? [] : [{ id: 'skupiny', label: 'Spravovat skupiny', Icon: Settings2, onClick: onManageSkupiny }]),
    ];

    const pill = (id, Icon, label, title, barva = SECTION_COLOR, upozorneni = false) => (
        <PohledPill key={id} Icon={Icon} label={label} pocet={pohledy[id].length}
            aktivni={pohled === id} upozorneni={upozorneni} onClick={() => setPohled(id)}
            title={title} isDark={isDark} mutedColor={mutedColor} barva={barva} kompaktni={kompaktni} />
    );

    const prazdnyText = {
        bezPriplatku: 'Všechny aktivní ingredience mají příplatek',
        marze: `Žádná ingredience nemá marži pod ${PRAH_MARZE.kriticka} %`,
        bezSkupiny: 'Všechny aktivní ingredience jsou zařazené ve skupině',
        bezKalkulace: 'Všechny aktivní ingredience mají kalkulaci',
    }[pohled];

    return (
        /* Na mobilu není seznam karta, ale rovnou plocha displeje — bez pozadí,
           průhledně na pozadí aplikace (viz SurovinyListPanel). */
        <div className={`overflow-hidden flex flex-col ${kompaktni ? '' : 'rounded-xl'}`}
            style={kompaktni ? { flex: 1, minHeight: 0 } : { ...cardStyle, flex: 1, minHeight: 0 }}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni}
                isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: search, onChange: setSearch, placeholder: 'Hledat ingredienci / kód…', placeholderKratky: 'Hledat / kód…' }}
                akce={akce}
                pod={vyber.vybrane.size > 0 && (
                    <div className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                        style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)' }}>
                        <span className="text-[11px] font-semibold" style={{ color: VYBER_COLOR }}>{pocetPolozek(vyber.vybrane.size)}</span>
                        <span className="text-[11px] flex-1 truncate" style={{ color: mutedColor }}>přetáhni do skupiny</span>
                        <button onClick={vyber.zrus} title="Zrušit výběr (Esc)"
                            className="flex items-center justify-center rounded-md"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, flexShrink: 0 }}>
                            <X size={13} />
                        </button>
                    </div>
                )}
                filtr={(
                    <FiltrTlacitko skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        pocetVysledku={zobrazene.length} siroky={siroky} isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                )}
                pohled={(
                    <>
                        {pill('aktivni', Check, 'Aktivní', 'Všechny ingredience v nabídce')}
                        {pill('bezPriplatku', Coins, 'Bez příplatku', 'Aktivní ingredience bez platné ceny — host je dostane zadarmo', MARZE_BARVY.kriticka, true)}
                        {pill('marze', TrendingDown, `Marže pod ${PRAH_MARZE.kriticka} %`, `Ingredience, kde marže některého kanálu klesla pod ${PRAH_MARZE.kriticka} %`, MARZE_BARVY.kriticka, true)}
                        {pill('bezSkupiny', FolderX, 'Bez skupiny', 'Ingredience, které nejsou v žádné skupině — nikde se nenabízí', SECTION_COLOR, true)}
                        {pill('bezKalkulace', Calculator, 'Bez kalkulace', 'Aktivní ingredience, u kterých chybí náklad')}
                        {pill('archiv', Archive, 'Archiv', 'Vyřazené ingredience')}
                    </>
                )}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                )}
            />

            {/* Pruh „N položek" a rozbalit vše na mobilu odpadá (§1b) — počet nese pill pohledu. */}
            {!loading && zobrazene.length > 0 && !kompaktni && (siroky
                ? <HlavickaMrizkyIngredienci isDark={isDark} sloupce={sloupce} rozbaleni={rozbaleni} />
                : <HlavickaUzka isDark={isDark} pocet={zobrazene.length} rozbaleni={rozbaleni} />)}

            {/* dragend uklidí stav i po puštění mimo cíl */}
            <div className="overflow-y-auto flex-1"
                onDragEnd={lzeTahat ? (() => { setDrag(null); setDragOverSkupina(null); }) : undefined}
                style={{
                    flex: 1, minHeight: 0, paddingTop: siroky ? 6 : 0,
                    ...(rezervaDole ? { paddingBottom: rezervaDole, overscrollBehavior: 'contain' } : null),
                }}>
                {loading ? (
                    <ListSkeleton isDark={isDark} />
                ) : zobrazene.length === 0 ? (
                    pocetFiltru > 0 ? (
                        <div className="text-center py-8">
                            <p className="text-xs" style={{ color: mutedColor }}>Žádná ingredience neodpovídá filtru</p>
                            <button type="button" onClick={zrusFiltry} className="text-xs font-semibold mt-2"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>Zrušit filtry</button>
                        </div>
                    ) : vRezimuArchiv ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Archiv je prázdný</p>
                    ) : prazdnyText ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>{prazdnyText}</p>
                    ) : searchActive ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Nic nenalezeno pro „{search}"</p>
                    ) : (
                        <div className="text-center px-6 py-10">
                            <PlusCircle size={28} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                            <p className="text-xs" style={{ color: mutedColor }}>Zatím žádné ingredience</p>
                            <button onClick={onNew} className="flex items-center gap-1 mt-3 mx-auto rounded-lg"
                                style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>
                                <Plus size={13} /> Vytvořit první
                            </button>
                        </div>
                    )
                ) : naplocho ? (
                    <div style={vRezimuArchiv ? { opacity: 0.8 } : undefined}>{zobrazene.map(it => radek(it, null, 0))}</div>
                ) : (
                    <div>
                        {skupinySorted.map(s => sekce(s, bySkupina.get(s.id) || []))}
                        {sekce(null, bez)}
                    </div>
                )}
            </div>
        </div>
    );
}
